import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

import { ICandidateDetails } from '@types';
import { CandidateDetailsQuestions } from '../CandidateDetailsQuestions/CandidateDetailsQuestions';
import { VideoIntro } from '../CandidateVideoIntro/CandidateVideoIntro';
import { InterviewFeedbackList } from './InterviewFeedbackList';
import { AssessmentBlock } from './components/AssessmentBlock';
import { InterviewerFeedback } from './components/InterviewerFeedback';
import { ProfileFacts } from './components/ProfileFacts';
import { Provenance } from './components/Provenance';
import { TestTaskBlock } from './components/TestTaskBlock';

export { ClosedBanner } from './components/ClosedBanner';

export interface ICandidateSection {
  title: string;
  content: React.ReactNode;
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="caption"
    component="h3"
    mb={1.5}
    fontWeight={600}
    letterSpacing="0.025em"
    textTransform="uppercase"
    color="text.secondary"
  >
    {children}
  </Typography>
);

const CardSectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography variant="subtitle1" component="h3" mb={1.5} color="text.primary">
    {children}
  </Typography>
);

export const CandidateSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Box
    sx={(theme) => ({
      px: 3,
      py: 2.5,
      borderBottom: `1px solid ${theme.palette.background.divider}`,
      '&:last-of-type': { borderBottom: 'none' },
    })}
  >
    <SectionTitle>{title}</SectionTitle>
    {children}
  </Box>
);

export const CandidateCardSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <Paper elevation={5} sx={{ padding: 2 }}>
    <CardSectionTitle>{title}</CardSectionTitle>
    {children}
  </Paper>
);

export const getCandidateVideo = (data: ICandidateDetails) =>
  data.videoInterview?.find((item) => item.recordFileName) ?? null;

export const getCandidateSections = (
  data: ICandidateDetails,
  { isViewOnly = false, includeVideo = true } = {},
): ICandidateSection[] => {
  const video = getCandidateVideo(data);

  return [
    {
      title: 'Assessment',
      content: <AssessmentBlock data={data} />,
      show: true,
    },
    {
      title: 'Feedback from our interviewers',
      content: <InterviewerFeedback data={data} />,
      show: Boolean(data.feedback?.feedback),
    },
    {
      title: 'Feedback from the interviews',
      content: <InterviewFeedbackList data={data} />,
      show: (data.interview ?? []).some((round) => round.result),
    },
    {
      title: 'Video intro',
      content: video ? <VideoIntro fileName={video.recordFileName!} /> : null,
      show: includeVideo && Boolean(video),
    },
    {
      title: 'Test task',
      content: <TestTaskBlock data={data} />,
      show: Boolean(data.testTasks?.[0]),
    },
    {
      title: 'Profile',
      content: <ProfileFacts data={data} />,
      show: Boolean(data.profile),
    },
    {
      title: 'Additional questions',
      content: <CandidateDetailsQuestions data={data} />,
      show: !isViewOnly && Boolean(data.submissionQuestions?.length),
    },
    {
      title: 'Submission',
      content: <Provenance data={data} />,
      show: !isViewOnly,
    },
  ]
    .filter((section) => section.show)
    .map(({ title, content }) => ({ title, content }));
};
